import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';
import type { Dance, Production } from '@pirouette/shared';

export const Route = createFileRoute('/faculty/productions')({
  component: FacultyProductions,
});

// Faculty's view of recital/comp data — what dances they're choreographer
// for, plus cast for those dances. RLS lets faculty SELECT cast_assignments
// studio-wide (Sprint 4 policy), so we filter client-side to dances where
// `choreographer_id` matches the logged-in instructor record.

interface DanceWithProduction extends Dance {
  productions: Production | null;
}

function FacultyProductions() {
  const myDances = useQuery({
    queryKey: ['faculty-dances'],
    queryFn: async (): Promise<DanceWithProduction[]> => {
      const user = await supabase.auth.getUser();
      const userId = user.data.user?.id;
      if (!userId) return [];
      const { data: instructorRow, error: e1 } = await supabase
        .from('instructors')
        .select('id')
        .eq('auth_user_id', userId)
        .maybeSingle();
      if (e1) throw e1;
      if (!instructorRow) return [];
      const { data, error } = await supabase
        .from('dances')
        .select('*, productions(*)')
        .eq('choreographer_id', instructorRow.id)
        .is('deleted_at', null);
      if (error) throw error;
      return ((data ?? []) as DanceWithProduction[]).filter(
        (d) => d.productions?.deleted_at === null,
      );
    },
  });

  // Group by production.
  const byProduction = new Map<string, DanceWithProduction[]>();
  for (const d of myDances.data ?? []) {
    if (!d.productions) continue;
    const list = byProduction.get(d.productions.id) ?? [];
    list.push(d);
    byProduction.set(d.productions.id, list);
  }

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          My choreography
        </p>
        <h1 className="mt-2">Productions</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Dances you're listed as choreographer on. Cast lists and stage times come from each
          production's lineup.
        </p>
      </header>

      {myDances.isLoading && <p aria-live="polite">Loading…</p>}

      {myDances.data?.length === 0 && (
        <Empty
          title="No choreography assignments yet"
          description="When the studio admin assigns you as choreographer on a dance, it'll appear here."
        />
      )}

      {byProduction.size > 0 && (
        <div className="space-y-6">
          {[...byProduction.entries()].map(([productionId, items]) => {
            const production = items[0]?.productions;
            return (
              <section key={productionId} aria-labelledby={`fp-${productionId}`}>
                <h2
                  id={`fp-${productionId}`}
                  className="font-[var(--font-display)] text-[var(--text-2xl)]"
                >
                  {production?.name}
                </h2>
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {production?.season}
                </p>
                <ul className="mt-4 grid gap-2">
                  {items.map((d) => (
                    <li key={d.id} className="surface-elevated p-4">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="font-medium">{d.name}</h3>
                        <Badge tone="neutral">{d.style}</Badge>
                      </div>
                      <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                        {Math.floor(d.length_seconds / 60)}:
                        {String(d.length_seconds % 60).padStart(2, '0')}
                        {d.music_title ? ` · ${d.music_title}` : ''}
                        {d.music_artist ? ` · ${d.music_artist}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
